import React from "react";
import { Box, Button } from "@mui/material";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import {
  ArrowRight,
  CheckCircle2,
  Users,
  Euro,
  Clock,
  ShieldCheck,
  TrendingUp,
  Building2,
} from "lucide-react";
import { useSelector } from "react-redux";
import CostCalculator from "bundles/LandingPageRecruiter/components/landing_page/CostCalculator";
import PricingSection from "bundles/LandingPageRecruiter/components/landing_page/PricingSection";
import ContactSection from "bundles/LandingPageRecruiter/components/landing_page/ContactSection";
import DashboardShowcase from "bundles/LandingPage/components/DashboardShowcase";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const highlights = [
  {
    icon: Euro,
    titleId: "alternative_recrutement.highlights.1.title",
    descriptionId: "alternative_recrutement.highlights.1.description",
  },
  {
    icon: Clock,
    titleId: "alternative_recrutement.highlights.2.title",
    descriptionId: "alternative_recrutement.highlights.2.description",
  },
  {
    icon: Users,
    titleId: "alternative_recrutement.highlights.3.title",
    descriptionId: "alternative_recrutement.highlights.3.description",
  },
];

const advantages = [
  {
    icon: Euro,
    titleId: "alternative_recrutement.advantages.1.title",
    descriptionId: "alternative_recrutement.advantages.1.description",
  },
  {
    icon: Users,
    titleId: "alternative_recrutement.advantages.2.title",
    descriptionId: "alternative_recrutement.advantages.2.description",
  },
  {
    icon: ShieldCheck,
    titleId: "alternative_recrutement.advantages.3.title",
    descriptionId: "alternative_recrutement.advantages.3.description",
  },
  {
    icon: TrendingUp,
    titleId: "alternative_recrutement.advantages.4.title",
    descriptionId: "alternative_recrutement.advantages.4.description",
  },
  {
    icon: Building2,
    titleId: "alternative_recrutement.advantages.5.title",
    descriptionId: "alternative_recrutement.advantages.5.description",
  },
  {
    icon: CheckCircle2,
    titleId: "alternative_recrutement.advantages.6.title",
    descriptionId: "alternative_recrutement.advantages.6.description",
  },
];

const AlternativeRecrutement = () => {
  const i18nLocale = useSelector((state) => state.i18nLocale);

  return (
    <>
      {/*Hero Section */}
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: "80px", md: "120px" },
          px: "24px",
          background: "hsl(178, 93%, 10%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 0%, hsla(145, 63%, 42%, 0.12) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            maxWidth: "800px",
            mx: "auto",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              px: "16px",
              py: "6px",
              borderRadius: "9999px",
              bgcolor: "hsla(145, 63%, 42%, 0.15)",
              color: "hsl(145, 63%, 49%)",
              fontSize: "0.75rem",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              mb: "24px",
            }}
          >
            <TrendingUp size={14} /> <FormattedMessage id="alternative_recrutement.hero.badge" />
          </Box>

          <Box
            component={motion.h1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            sx={{
              fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(0, 0%, 98%)",
              lineHeight: 1.1,
              mt: 0,
              mb: "24px",
            }}
          >
            <FormattedMessage id="alternative_recrutement.hero.title" />{" "}
            <Box component="span" sx={{ color: "hsl(145, 63%, 49%)" }}>
              <FormattedMessage id="alternative_recrutement.hero.title_highlight" />
            </Box>
          </Box>

          <Box
            component={motion.p}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsla(0, 0%, 98%, 0.7)",
              maxWidth: "640px",
              mx: "auto",
              lineHeight: 1.6,
              mt: 0,
              mb: "40px",
            }}
          >
            <FormattedMessage id="alternative_recrutement.hero.description" />
          </Box>

          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            sx={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}
          >
            <Button
              href={`/${i18nLocale}/recruiters/sign_up`}
              variant="contained"
              sx={{
                bgcolor: "hsl(145, 63%, 42%)",
                color: "hsl(0, 0%, 98%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                fontSize: "1rem",
                textTransform: "none",
                borderRadius: "8px",
                px: "32px",
                py: "14px",
                "&:hover": { bgcolor: "hsl(145, 63%, 36%)" },
              }}
            >
              <FormattedMessage id="alternative_recrutement.hero.cta_try" /> <ArrowRight size={18} style={{ marginLeft: "8px" }} />
            </Button>
            <Button
              href="#comparateur"
              variant="outlined"
              sx={{
                borderColor: "hsla(0, 0%, 98%, 0.2)",
                color: "hsl(0, 0%, 98%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                fontSize: "1rem",
                textTransform: "none",
                borderRadius: "8px",
                px: "32px",
                py: "14px",
                "&:hover": {
                  borderColor: "hsl(145, 63%, 42%)",
                  color: "hsl(145, 63%, 49%)",
                  bgcolor: "transparent",
                },
              }}
            >
              <FormattedMessage id="alternative_recrutement.hero.cta_compare" />
            </Button>
          </Box>
        </Box>
      </Box>

      {/*Problem Section */}
      <Box
        component="section"
        sx={{
          py: { xs: "64px", md: "96px" },
          px: "24px",
          bgcolor: "hsl(0, 0%, 100%)",
        }}
      >
        <Box sx={{ maxWidth: "1100px", mx: "auto" }}>
          <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: "center", mb: "56px" }}
          >
            <Box
              component="h2"
              sx={{
                fontSize: { xs: "1.5rem", md: "2.25rem" },
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
                color: "hsl(220, 25%, 12%)",
                mt: 0,
                mb: "16px",
              }}
            >
              <FormattedMessage id="alternative_recrutement.why_section.title" />
            </Box>
            <Box
              component="p"
              sx={{
                fontSize: "1.125rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(220, 10%, 46%)",
                maxWidth: "640px",
                mx: "auto",
                mt: 0,
                mb: 0,
              }}
            >
              <FormattedMessage id="alternative_recrutement.why_section.description" />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: "24px",
            }}
          >
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <Box
                  key={item.titleId}
                  component={motion.div}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  sx={{
                    p: "32px",
                    borderRadius: "16px",
                    border: "1px solid hsl(220, 14%, 93%)",
                    bgcolor: "hsl(220, 14%, 98%)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <Box
                    sx={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      bgcolor: "hsla(145, 63%, 42%, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "hsl(145, 63%, 42%)",
                    }}
                  >
                    <Icon size={24} />
                  </Box>
                  <Box
                    component="h3"
                    sx={{
                      fontSize: "1.125rem",
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      color: "hsl(220, 25%, 12%)",
                      mt: 0,
                      mb: 0,
                    }}
                  >
                    <FormattedMessage id={item.titleId} />
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      fontSize: "0.9375rem",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      color: "hsl(220, 10%, 46%)",
                      lineHeight: 1.6,
                      mt: 0,
                      mb: 0,
                    }}
                  >
                    <FormattedMessage id={item.descriptionId} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/*Solution / Who We Are Section */}
      <Box
        component="section"
        sx={{
          py: { xs: "64px", md: "96px" },
          px: "24px",
          background: "hsl(178, 93%, 10%)",
        }}
      >
        <Box sx={{ maxWidth: "1100px", mx: "auto" }}>
          <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            sx={{ textAlign: "center", mb: "56px" }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                px: "16px",
                py: "6px",
                borderRadius: "9999px",
                bgcolor: "hsla(145, 63%, 42%, 0.15)",
                color: "hsl(145, 63%, 49%)",
                fontSize: "0.75rem",
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                mb: "16px",
              }}
            >
              <ShieldCheck size={14} /> <FormattedMessage id="alternative_recrutement.solution.badge" />
            </Box>
            <Box
              component="h2"
              sx={{
                fontSize: { xs: "1.5rem", md: "2.25rem" },
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
                color: "hsl(0, 0%, 98%)",
                mt: 0,
                mb: "16px",
              }}
            >
              <FormattedMessage id="alternative_recrutement.solution.title" />
            </Box>
            <Box
              component="p"
              sx={{
                fontSize: "1.125rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsla(0, 0%, 98%, 0.7)",
                maxWidth: "680px",
                mx: "auto",
                lineHeight: 1.6,
                mt: 0,
                mb: 0,
              }}
            >
              <FormattedMessage id="alternative_recrutement.solution.description" />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
              gap: "24px",
            }}
          >
            {advantages.map((advantage, i) => {
              const Icon = advantage.icon;
              return (
                <Box
                  key={advantage.titleId}
                  component={motion.div}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  sx={{
                    p: "28px",
                    borderRadius: "16px",
                    border: "1px solid hsla(0, 0%, 98%, 0.1)",
                    bgcolor: "hsla(0, 0%, 98%, 0.05)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: "hsla(145, 63%, 42%, 0.4)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "10px",
                      bgcolor: "hsla(145, 63%, 42%, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "hsl(145, 63%, 49%)",
                    }}
                  >
                    <Icon size={22} />
                  </Box>
                  <Box
                    component="h3"
                    sx={{
                      fontSize: "1rem",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      color: "hsl(0, 0%, 98%)",
                      mt: 0,
                      mb: 0,
                    }}
                  >
                    <FormattedMessage id={advantage.titleId} />
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      fontSize: "0.875rem",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      color: "hsla(0, 0%, 98%, 0.6)",
                      lineHeight: 1.6,
                      mt: 0,
                      mb: 0,
                    }}
                  >
                    <FormattedMessage id={advantage.descriptionId} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/*Cost Calculator */}
      <Box id="comparateur">
        <CostCalculator />
      </Box>

      <Box>
        <DashboardShowcase />
      </Box>

      {/*Pricing */}
      <PricingSection />

      {/*CTA Section */}
      <Box
        component="section"
        sx={{
          py: { xs: "64px", md: "96px" },
          px: "24px",
          background: "hsl(178, 93%, 10%)",
          textAlign: "center",
          borderBottom: "1px solid hsl(0 0% 100% / 0.1)",
        }}
      >
        <Box sx={{ maxWidth: "680px", mx: "auto" }}>
          <Box
            component={motion.h2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            sx={{
              fontSize: { xs: "1.5rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(0, 0%, 98%)",
              mt: 0,
              mb: "16px",
            }}
          >
            <FormattedMessage id="alternative_recrutement.cta.title" />
          </Box>
          <Box
            component={motion.p}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            sx={{
              fontSize: "1.125rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              color: "hsla(0, 0%, 98%, 0.7)",
              lineHeight: 1.6,
              mt: 0,
              mb: "40px",
            }}
          >
            <FormattedMessage id="alternative_recrutement.cta.description" />
          </Box>
          <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={2}
            sx={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}
          >
            <Button
              href={`/${i18nLocale}/recruiters/sign_up`}
              variant="contained"
              sx={{
                bgcolor: "hsl(145, 63%, 42%)",
                color: "hsl(0, 0%, 98%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                fontSize: "1rem",
                textTransform: "none",
                borderRadius: "8px",
                px: "32px",
                py: "14px",
                "&:hover": { bgcolor: "hsl(145, 63%, 36%)" },
              }}
            >
              <FormattedMessage id="alternative_recrutement.cta.button" /> <ArrowRight size={18} style={{ marginLeft: "8px" }} />
            </Button>
          </Box>
        </Box>
      </Box>

      {/*Contact */}
      <ContactSection type="recruiter" />
    </>
  );
};

export default AlternativeRecrutement;
